export const PLAYLIST_COVER_EDGE = 320;
export const MAX_PLAYLIST_COVER_TEXT = 48 * 1024;
export const MAX_PLAYLIST_COVERS_TEXT = 512 * 1024;
const MAX_INPUT_BYTES = 12 * 1024 * 1024;
const MAX_INPUT_PIXELS = 40_000_000;

export type PlaylistCoverFields = { customCover?: string; coverUpdatedAt?: string };

function imageDimensions(bytes: Uint8Array, mime: string): { width: number; height: number } | null {
  if (mime === 'image/png') {
    const signature = [137, 80, 78, 71, 13, 10, 26, 10];
    if (bytes.length < 45 || !signature.every((value, index) => bytes[index] === value) || String.fromCharCode(...bytes.slice(12, 16)) !== 'IHDR') return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (mime !== 'image/jpeg' || bytes.length < 12 || bytes[0] !== 255 || bytes[1] !== 216 || bytes.at(-2) !== 255 || bytes.at(-1) !== 217) return null;
  for (let offset = 2; offset + 8 < bytes.length;) {
    if (bytes[offset++] !== 255) return null;
    while (bytes[offset] === 255) offset++;
    const marker = bytes[offset++];
    if (marker === 218 || marker === 217) break;
    const size = bytes[offset] * 256 + bytes[offset + 1];
    if (size < 2 || offset + size > bytes.length) return null;
    if ([192, 193, 194, 195, 197, 198, 199, 201, 202, 203, 205, 206, 207].includes(marker)) return { height: bytes[offset + 3] * 256 + bytes[offset + 4], width: bytes[offset + 5] * 256 + bytes[offset + 6] };
    offset += size;
  }
  return null;
}

/** A dedicated raster field; URLs and generic metadata never bypass filtering. */
export function assertPlaylistCover(value: unknown): string {
  if (typeof value !== 'string' || value.length > MAX_PLAYLIST_COVER_TEXT) throw new Error('歌单封面超过 48 KiB 限制');
  const match = /^data:(image\/(?:jpeg|png));base64,([A-Za-z0-9+/]+={0,2})$/.exec(value);
  if (!match || match[2].length % 4 !== 0) throw new Error('歌单封面必须是有效的 JPEG 或 PNG 图片');
  let bytes: Uint8Array;
  try { bytes = Uint8Array.from(atob(match[2]), character => character.charCodeAt(0)); }
  catch { throw new Error('歌单封面编码无效'); }
  const size = imageDimensions(bytes, match[1]);
  if (!size || size.width !== PLAYLIST_COVER_EDGE || size.height !== PLAYLIST_COVER_EDGE) throw new Error('歌单封面必须是 320 × 320 的方形图片');
  return value;
}

export function playlistCoverFields(value: Record<string, unknown>): PlaylistCoverFields {
  const result: PlaylistCoverFields = {};
  if (value.customCover !== undefined && value.customCover !== null && value.customCover !== '') result.customCover = assertPlaylistCover(value.customCover);
  if (value.coverUpdatedAt !== undefined && value.coverUpdatedAt !== null && value.coverUpdatedAt !== '') {
    if (typeof value.coverUpdatedAt !== 'string' || value.coverUpdatedAt.length > 128 || !Number.isFinite(Date.parse(value.coverUpdatedAt))) throw new Error('歌单封面时间无效');
    result.coverUpdatedAt = new Date(value.coverUpdatedAt).toISOString();
  }
  return result;
}

export function assertPlaylistCoverBudget(playlists: PlaylistCoverFields[]) {
  let total = 0;
  for (const playlist of playlists) {
    playlistCoverFields(playlist as Record<string, unknown>);
    total += playlist.customCover?.length || 0;
    if (total > MAX_PLAYLIST_COVERS_TEXT) throw new Error('自定义歌单封面合计超过 512 KiB 限制，请先恢复部分歌单的自动封面');
  }
}

/** Invalid optional artwork must not discard an otherwise usable old library. */
export function readPlaylistCoverRecords<T extends PlaylistCoverFields>(playlists: T[]): T[] {
  let total = 0;
  return playlists.map(playlist => {
    const { customCover: _cover, coverUpdatedAt: _revision, ...base } = playlist;
    try {
      const fields = playlistCoverFields(playlist as Record<string, unknown>);
      const size = fields.customCover?.length || 0;
      if (total + size > MAX_PLAYLIST_COVERS_TEXT) return base as T;
      total += size;
      return { ...base, ...fields } as T;
    } catch { return base as T; }
  });
}

async function decodeRaster(blob: Blob) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image(); image.src = url;
    await image.decode();
    return { width: image.naturalWidth, height: image.naturalHeight, image, close() {} };
  } finally { URL.revokeObjectURL(url); }
}

export async function verifyPlaylistCover(value: string) {
  assertPlaylistCover(value);
  const match = /^data:([^;]+);base64,(.+)$/.exec(value)!;
  const bytes = Uint8Array.from(atob(match[2]), character => character.charCodeAt(0));
  const image = await decodeRaster(new Blob([bytes], { type: match[1] })).catch(() => { throw new Error('歌单封面图片无法解码，原封面已保留'); });
  try { if (image.width !== PLAYLIST_COVER_EDGE || image.height !== PLAYLIST_COVER_EDGE) throw new Error('歌单封面尺寸无效'); }
  finally { image.close(); }
}

/** Decode and center-crop before publishing anything to the playlist snapshot. */
export async function cropPlaylistCover(file: File): Promise<string> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || !file.size || file.size > MAX_INPUT_BYTES) throw new Error('请选择不超过 12 MiB 的 JPEG、PNG 或 WebP 图片');
  const image = await decodeRaster(file).catch(() => { throw new Error('图片无法读取，原封面已保留'); });
  try {
    if (!image.width || !image.height || image.width * image.height > MAX_INPUT_PIXELS) throw new Error('图片像素过大，请选择不超过 4000 万像素的图片');
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = PLAYLIST_COVER_EDGE;
    const context = canvas.getContext('2d'); if (!context) throw new Error('浏览器无法处理图片');
    const edge = Math.min(image.width, image.height);
    context.drawImage('image' in image ? image.image : image, (image.width - edge) / 2, (image.height - edge) / 2, edge, edge, 0, 0, PLAYLIST_COVER_EDGE, PLAYLIST_COVER_EDGE);
    for (const quality of [.86, .74, .6, .45]) {
      const value = canvas.toDataURL('image/jpeg', quality);
      if (value.length <= MAX_PLAYLIST_COVER_TEXT) return assertPlaylistCover(value);
    }
    throw new Error('图片压缩后仍超过 48 KiB，请选择内容更简单的图片');
  } finally { image.close(); }
}
